import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { SEO } from '../../components/common/SEO';
import { motion } from 'framer-motion';
import { pressReleases } from '../../data/pressReleases';
import PressReleaseList from '../../components/press/PressReleaseList';
import MediaGallery from '../../components/press/MediaGallery';
import { pressKitGenerator } from '../../utils/pressKitGenerator';

import {
  Newspaper,
  Download,
  Mail,
  Phone,
  Package,
  Image as ImageIcon,
  FileText,
  Share2,
  Loader2
} from 'lucide-react';

const Press = () => {
  const { isDarkMode } = useTheme();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPressKit = async () => {
    try {
      setIsDownloading(true);
      await pressKitGenerator.downloadPressKit();
    } catch (error) {
      console.error('Failed to download press kit:', error);
      // You could add a toast notification here
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}>
      <SEO 
        title="Newsroom | Press Room - Latest News & Media Kit"
        description="Access the latest Newsroom press releases, company news, and downloadable media kit including logos, brand assets, and marketing materials."
        keywords={["press releases", "news", "media kit", "brand assets", "logos", "newsroom company news"]}
      />

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-blue-600/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 relative">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex justify-center items-center mb-6">
              <div className="p-3 rounded-full bg-blue-500/10 backdrop-blur-sm">
                <Newspaper className={`w-10 h-10 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
            </div>
            <h1 className={`text-4xl md:text-5xl font-bold mb-4 font-heading tracking-tighter ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Press Room
            </h1>
            <p className={`text-xl max-w-3xl mx-auto mb-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Latest news, announcements, and comprehensive media resources from Newsroom
            </p>
            
            {/* Quick Actions */}
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={handleDownloadPressKit}
                disabled={isDownloading}
                className="inline-flex items-center px-6 py-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Download className="w-5 h-5 mr-2" />
                )}
                {isDownloading ? 'Generating Press Kit...' : 'Download Complete Press Kit'}
              </button>
              <a
                href="mailto:hello@news-room.ca"
                className={`inline-flex items-center px-6 py-3 rounded-xl ${
                  isDarkMode
                    ? 'bg-gray-800 text-white hover:bg-gray-700 border border-gray-700'
                    : 'bg-white text-gray-900 hover:bg-gray-50 border border-gray-200'
                } transition-all shadow-sm hover:shadow-md`}
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact Press Team
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <nav className="flex space-x-8">
            <a
              href="#releases"
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                isDarkMode
                  ? 'border-blue-400 text-blue-400'
                  : 'border-blue-600 text-blue-600'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Press Releases
            </a>
            <a
              href="#media-kit"
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors border-transparent ${
                isDarkMode
                  ? 'text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Package className="w-4 h-4 inline mr-2" />
              Media Kit
            </a>
            <a
              href="#assets"
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors border-transparent ${
                isDarkMode
                  ? 'text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ImageIcon className="w-4 h-4 inline mr-2" />
              Brand Assets
            </a>
            <a
              href="#contact"
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors border-transparent ${
                isDarkMode
                  ? 'text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Share2 className="w-4 h-4 inline mr-2" />
              Media Contact
            </a>
          </nav>
        </div>
      </div>

      {/* Press Releases Section */}
      <section id="releases" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <PressReleaseList releases={pressReleases} />
      </section>

      {/* Media Kit Section */}
      <section id="media-kit" className={`${isDarkMode ? 'bg-gray-800/50' : 'bg-white/50'} py-16`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MediaGallery onDownloadAll={handleDownloadPressKit} isDownloading={isDownloading} />
        </div>
      </section>

      {/* Company Information */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* About Newsroom */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className={`text-2xl font-bold mb-6 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              About Newsroom
            </h2>
            <div className={`prose max-w-none ${
              isDarkMode ? 'prose-invert text-gray-300' : 'text-gray-600'
            }`}>
              <p className="mb-4">
                Founded in 2021, Newsroom is revolutionizing financial market research through 
                advanced AI technology. Our platform combines cutting-edge artificial intelligence 
                with real-time market data to provide institutional-grade analysis tools for 
                traders and investors of all levels.
              </p>
              <p className="mb-4">
                Based in Victoria, BC, we're committed to democratizing access to sophisticated 
                financial analysis tools and empowering individual investors with the same 
                capabilities traditionally reserved for institutional players.
              </p>
              <p>
                Our AI-powered platform leverages Claude 3.5 technology to transform complex 
                market data into actionable insights, making professional-grade market research 
                accessible to everyone.
              </p>
            </div>
          </motion.div>

          {/* Key Facts */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className={`rounded-2xl border p-8 ${
              isDarkMode 
                ? 'bg-gray-800/70 border-gray-700/50' 
                : 'bg-white border-gray-200'
            }`}
          >
            <h3 className={`text-xl font-bold mb-6 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Company Facts
            </h3>
            <dl className="space-y-4">
              <div>
                <dt className={`text-sm font-medium ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Founded
                </dt>
                <dd className={`text-lg font-semibold ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  2021
                </dd>
              </div>
              <div>
                <dt className={`text-sm font-medium ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Headquarters
                </dt>
                <dd className={`text-lg font-semibold ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Victoria, BC, Canada
                </dd>
              </div>
              <div>
                <dt className={`text-sm font-medium ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Industry
                </dt>
                <dd className={`text-lg font-semibold ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Financial Technology
                </dd>
              </div>
              <div>
                <dt className={`text-sm font-medium ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Website
                </dt>
                <dd className={`text-lg font-semibold ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                }`}>
                  <a href="https://news-room.ca" target="_blank" rel="noopener noreferrer">
                    news-room.ca
                  </a>
                </dd>
              </div>
            </dl>
          </motion.div>
        </div>
      </section>

      {/* Press Contact */}
      <section id="contact" className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-gray-50/80'} py-16 backdrop-blur-sm`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className={`p-8 rounded-2xl border ${
              isDarkMode 
                ? 'bg-gray-900/70 border-gray-700/50' 
                : 'bg-white/80 border-gray-200'
            } text-center shadow-lg backdrop-blur-sm`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className={`text-2xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Media Contact
            </h2>
            
            <p className={`mb-8 max-w-2xl mx-auto ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              For press inquiries, interview requests, partnership opportunities, or additional 
              information about Newsroom's AI-powered market research platform, please contact:
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-10">
              <a
                href="mailto:hello@news-room.ca"
                className="inline-flex items-center justify-center group"
              >
                <Mail className={`w-5 h-5 mr-3 ${
                  isDarkMode ? 'text-blue-400 group-hover:text-blue-300' : 'text-blue-500 group-hover:text-blue-600'
                } transition-colors`} />
                <span className={`${
                  isDarkMode 
                    ? 'text-gray-200 group-hover:text-white' 
                    : 'text-gray-700 group-hover:text-gray-900'
                } transition-colors`}>
                  hello@news-room.ca
                </span>
              </a>
              
              <a
                href="tel:+17787510983"
                className="inline-flex items-center justify-center group"
              >
                <Phone className={`w-5 h-5 mr-3 ${
                  isDarkMode ? 'text-blue-400 group-hover:text-blue-300' : 'text-blue-500 group-hover:text-blue-600'
                } transition-colors`} />
                <span className={`${
                  isDarkMode 
                    ? 'text-gray-200 group-hover:text-white' 
                    : 'text-gray-700 group-hover:text-gray-900'
                } transition-colors`}>
                  +1 (778) 751-0983
                </span>
              </a>
            </div>

            {/* Press Kit Download */}
            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
              <p className={`text-sm mb-4 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Need immediate access to our brand assets?
              </p>
              <button
                onClick={handleDownloadPressKit}
                disabled={isDownloading}
                className={`inline-flex items-center px-6 py-3 rounded-xl border transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-800 hover:border-gray-500'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                }`}
              >
                {isDownloading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Download className="w-5 h-5 mr-2" />
                )}
                {isDownloading ? 'Generating...' : 'Download Press Kit'}
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Press;